import { Injectable } from '@angular/core';
import { GlobalsService } from './globals.service';
import { GlobalsService as GlobalsServiceMicro } from '@app/services/micro/globals.service';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { LocalDataService } from '../local-data.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import {
  ESTATUS_CHECKIN,
  FARES_FAMILY,
  MISSING_CHECKIN_REQUIRED_FIELDS,
  PAX_TYPE,
  SeatPassangerEstatus,
  StatusBag,
} from '@app/enums';
import { ApiService } from './api.service';
import { CountriesInt, PassportScanInfoInt } from '@app/interfaces';
import { PassportInfoInt } from '@app/models/micro/updateDocuments.model';
import { MissingCheckinRequiredFields } from '@app/models/micro/response/viewDocuments.model';
import { MessagesService } from '../messages.service';
import { TypesErrors } from '@app/enums/errors.enum';
import {
  getSeatRouteByPassengerCount,
  messageParamsUpdateDocuments,
  pendingPassportPassengers,
} from '@app/utils';
import { BehaviorSubject } from 'rxjs';
import { ErrorModel } from '@models/micro/error.model';
import { RespBenefits } from '@app/models/micro/response/respBenefits.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BagsRedemptionModalComponent } from '@app/shared/modals/bags-redemption-modal/bags-redemption-modal.component';
import { normalizeGender } from '@utils/functions/normalizeGender';
import { BenefitOfferModalComponent } from '@app/shared/modals/benefit-offer-modal/benefit-offer-modal.component';
import { RetryErrorModalComponent } from '@app/shared/modals/retry-error-modal/retry-error-modal.component';

/**
 * Servicio encargado de la gestión de pasajeros en una reserva.
 * Proporciona métodos para actualizar información de pasajeros,
 * manejar selecciones y verificar documentos requeridos para el check-in.
 */
@Injectable({
  providedIn: 'root',
})
export class PassengerService {
  //selectedPassengersToBags: Passenger[] = [];
  /**
   * Lista de pasajeros seleccionados.
   */

  passengersSelections: Passenger[];
  totalPassengersSelectNoDocs: number = 1;
  paxStepNac: number = 0;
  private currentPassenger: Passenger;
  private passportVerificationQueue: string[] = [];
  private paxStepIntSubject = new BehaviorSubject<number>(1);
  paxStepInt$ = this.paxStepIntSubject.asObservable();

  constructor(
    private _globalService: GlobalsService,
    private _globalServiceMicro: GlobalsServiceMicro,
    private _message: MessagesService,
    private _modalService: NgbModal,
    private _router: Router
  ) {
    // intentional empty: dependencies are injected as parameter properties
  }

  /**
   * Actualiza la selección de pasajeros con base en la reserva.
   * @param lookUp Lista de reservas a evaluar.
   */

  updateSelectionsPassenger(lookUp: Reservation[]) {
    const reservationSelection = this._globalService.getReservation();
    const reservarionUpdate = lookUp.find(
      (reservation) =>
        reservation.flightSummary.arrivalAirport ===
          reservationSelection.flightSummary.arrivalAirport &&
        reservation.flightSummary.departureAirport ===
          reservationSelection.flightSummary.departureAirport
    );
    this._globalService.setPNRLookUp(lookUp);
    this._globalService.setReservation(reservarionUpdate);
    this.setPassengerSelection(this.passengersSelections);
  }

  /**
   * Actualiza la información de un pasajero seleccionado.
   * @param property Propiedad a modificar.
   * @param value Nuevo valor.
   */

  updatePassengerInfo(property: any, value: any) {
    if (this.currentPassenger) {
      this.currentPassenger.passengerInfo[property] = value;
    }
    this._globalService.setPassenger(this.currentPassenger);
  }

  /**
   * Obtiene el tipo de pasajero en formato abreviado.
   * @returns String representando el tipo de pasajero.
   */

  getPaxType() {
    let paxType = '';
    if (this.currentPassenger?.paxType === PAX_TYPE.ADULT) {
      paxType = 'ADT';
    } else if (this.currentPassenger?.paxType === PAX_TYPE.CHILD) {
      paxType = 'CHD';
    } else if (this.currentPassenger?.paxType === PAX_TYPE.INFANT) {
      paxType = 'INF';
    }
    return paxType;
  }

  /**
   * Obtiene la posición actual del pasajero en la selección.
   * @returns Índice del pasajero en la selección.
   */

  getPositionCurrentPassenger(segmentCode?: string) {
    if (segmentCode) {
      const positionPax = sessionStorage.getItem(`positionCurrentPassenger_${segmentCode}`);
      return positionPax ? JSON.parse(positionPax) : 0;
    }
    const positionPax = sessionStorage.getItem('positionCurrentPassenger');
    return positionPax ? JSON.parse(positionPax) : undefined;
  }

  /**
   * Guarda la posición actual del pasajero en la sesión.
   * @param position Posición del pasajero en la lista.
   */

  setPositionCurrentPassenger(position, segmentCode?: string) {
    if (segmentCode) {
      sessionStorage.setItem(`positionCurrentPassenger_${segmentCode}`, JSON.stringify(position));
    }
    sessionStorage.setItem('positionCurrentPassenger', JSON.stringify(position));
  }

  /**
   * Retorna el pasajero actualmente seleccionado.
   * @returns Objeto del pasajero seleccionado.
   */

  getCurrentPassenger(segmentCode?: string) {
    const position = this.getPositionCurrentPassenger(segmentCode);
    const passagers = this.getPassengerSelection();
    return passagers[position];
  }

  /**
   * Establece la lista de pasajeros seleccionados para check-in.
   * @param passengers Lista de pasajeros a marcar como seleccionados.
   */

  setPassengerSelection(passengers: Array<Passenger>): void {
    const allPassengers = this._globalService.getPassengers();
    this.passengersSelections = [];
    for (let passenger of allPassengers) {
      passenger.checkinSelected = false;
      if (passengers.find((x) => x.id === passenger.id)) {
        passenger.checkinSelected = true;
        this.passengersSelections.push(passenger);
      }
      this._globalService.setPassenger(passenger);
    }
  }

  /**
   * Obtiene los pasajeros seleccionados, opcionalmente incluyendo infantes.
   * @param includeInfant Si se incluyen infantes en la lista.
   * @returns Lista de pasajeros seleccionados.
   */

  getPassengerSelection(includeInfant = false): Array<Passenger> {
    const allPassengers = this._globalServiceMicro.getPassengers();
    return includeInfant
      ? allPassengers.filter((x) => x.checkinSelected)
      : allPassengers.filter((x) => x.checkinSelected && x.paxType !== PAX_TYPE.INFANT);
  }

  getSeatRoute(passengers?: Passenger[]): string {
    return getSeatRouteByPassengerCount();
  }

  setSelectCurrentPassenger(passenger: Passenger): void {
    this.currentPassenger = passenger;
  }

  getSelectCurrentPassenger() {
    return this.currentPassenger;
  }

  /**
   * CI-23237: Fija al pasajero con informacion de contacto faltante como pasajero
   * actual y redirige a la pantalla de captura para completarla antes de continuar
   * con el check-in. Centraliza la logica compartida por los flujos normal, Standby
   * y Seamless.
   */
  redirectToContactInformation(passenger: Passenger): void {
    const passengersSelectNoInfo = this.getPassengerSelection().filter(
      (p) => !p.checkinStatus && !p.isCompleteInformationContact()
    );
    this.totalPassengersSelectNoDocs = passengersSelectNoInfo.length;

    const reservation = this._globalService.getReservation();
    const indexPassenger = reservation.passengers.findIndex((p) => p.id === passenger.id);
    this.setSelectCurrentPassenger(passenger);
    this.setPositionCurrentPassenger(indexPassenger);
    this._router.navigate(['/pax/contactInformation']);
  }

  get findPaxSkyPriority() {
    return !!this._globalService.getPassengers().find((pax) => pax.isSkyPriority);
  }

  setSelectPassengersToBags(passengers: Passenger[]) {
    this.getPassengerSelection().map((p) => {
      passengers.map((psg) => {
        if (p.id === psg.id) {
          p.isSelectForBagagge = true;
          this._globalServiceMicro.setPassenger(p);
        }
      });
    });
  }

  getSelectPassengersToBags() {
    return this.getPassengerSelection().filter((p) => p.isSelectForBagagge) || [];
  }

  getSelectPassengersToSeats() {
    return (
      this.getPassengerSelection().filter((p) =>
        p?.seatsNew.some((s) => !s?.paidSeat || s?.status === SeatPassangerEstatus.PENDING_PAYMENT)
      ) || []
    );
  }

  get passengersToBags() {
    return this._globalService
      .getPassengers()
      .filter((p) => p.checkinSelected && p.paxType !== PAX_TYPE.INFANT && p.isSelectForBagagge);
  }
  hasPassengersVerifyTravelDocuments() {
    const passengers = this.getPassengerSelection().find((p) => p.hasVerifyDocument);
    return !!passengers;
  }

  /**
   * Mapea la información escaneada del pasaporte a la estructura del modelo de pasaporte.
   * @param passportInfo Información escaneada del pasaporte.
   * @returns Objeto con la información formateada del pasaporte.
   */

  /**
   * Mapea la información escaneada del pasaporte al modelo de documento de pasaporte.
   * Incluye los campos requeridos por CBP: firstName, lastName, gender, birthDate.
   * @param passportInfo Información del pasaporte escaneado
   * @returns Documento de pasaporte mapeado con todos los campos
   */
  mapPassportInfoToPassportDocument(passportInfo: PassportScanInfoInt): PassportInfoInt {
    // CI-17097: Mapeo de campos adicionales desde escaneo de pasaporte
    // Incluye: firstName, lastName, gender, birthDate
    const birthDate =
      passportInfo.dob ||
      (passportInfo.birthDateYear && passportInfo.birthDateMonth && passportInfo.birthDateDay
        ? `${passportInfo.birthDateYear}-${passportInfo.birthDateMonth.padStart(
            2,
            '0'
          )}-${passportInfo.birthDateDay.padStart(2, '0')}`
        : undefined);

    let passportDocument: PassportInfoInt = {
      firstName: passportInfo.firstName,
      lastName: passportInfo.lastName,
      gender: normalizeGender(passportInfo.gender),
      birthDate,
      documentNumber: passportInfo.number,
      issuingCountry: passportInfo.issuingCountry,
      nationality: passportInfo.country,
      passportScanned: true,
      expirationDate: `${
        passportInfo.expirationDateYear
      }-${passportInfo?.expirationDateMonth?.padStart(
        2,
        '0'
      )}-${passportInfo?.expirationDateDay?.padStart(2, '0')}`,
    };

    return passportDocument;
  }

  /**
   * Construye los campos obligatorios faltantes para el check-in de un pasajero.
   * @param currentPassenger Pasajero actual.
   * @param passengerDoc Información del pasajero.
   * @param response Respuesta del servicio con datos de pasajeros.
   */

  buildMissingCheckinRequiredFields(currentPassanger: Passenger, passegerdoc, response) {
    const omitMissingCheckinRequiredFields = [
      'PASSENGER_TRACING_CONTACT_PHONE',
      'PASSENGER_TRACING_CONTACT_EMAIL',
      'COUNTRY_OF_RESIDENCE',
      'COUNTRY_OF_RESIDENCE/INF',
      'EMAIL',
      'PHONE',
      'IGPP',
      'TIM',
      'ESTA',
      'ETA',
      'DHS',
    ];
    const passengerINF = this._globalService
      .getPassengers()
      .find((p) => p.nameRefNumber === currentPassanger.passengerInfant);
    currentPassanger.missingCheckinRequiredFields = [];
    if (currentPassanger.passengerInfo.passport) {
      for (const doc of passegerdoc.missingCheckinRequiredFields) {
        const hasTimaticInfo = !!currentPassanger.passengerInfo?.timaticRQ?.returnDate;
        const finDocument = !!currentPassanger.missingCheckinRequiredFields.find(
          (docu) => docu.document === doc.document
        );
        const isReturnDateDoc = doc.document?.toUpperCase().includes('RETURN_DATE');
        if (
          !omitMissingCheckinRequiredFields.includes(doc.document) &&
          !finDocument &&
          !(hasTimaticInfo && isReturnDateDoc)
        ) {
          currentPassanger.missingCheckinRequiredFields.push(doc);
        }
      }
    }
    if (currentPassanger.passengerInfant) {
      const passengerINF = response.passengers.find(
        (p) => p.passengerNameNumber === currentPassanger.passengerInfant
      );
      const hasTIMINF = passengerINF?.missingCheckinRequiredFields?.some(
        (item) => item.document === 'TIM/INF'
      );
      if (!hasTIMINF) {
        for (const doc of passengerINF.missingCheckinRequiredFields?.filter(
          (doc) =>
            doc.document.includes('INF') &&
            !omitMissingCheckinRequiredFields.includes(doc.document) &&
            !(
              doc.document === 'DESTINATION_ADDRESS/INF' &&
              !passengerINF.passengerInfo?.passport?.documentNumber
            )
        ) || []) {
          const validatePassportInf = currentPassanger.missingCheckinRequiredFields.find(
            (docu) => docu.document === 'PASSPORT/INF'
          );
          if (validatePassportInf === undefined) {
            currentPassanger.missingCheckinRequiredFields.push(doc);
          }
        }
      }
    } else if (currentPassanger.paxType === PAX_TYPE.INFANT) {
      const passengerADT = this._globalService
        .getPassengers()
        .find((p) => p.passengerInfant === currentPassanger.nameRefNumber);
      const hasTIMINF = passengerADT?.missingCheckinRequiredFields?.some(
        (item) => item.document.includes('INF') && item.document !== 'PASSPORT/INF'
      );

      if (!hasTIMINF && currentPassanger.passengerInfo.passport) {
        for (const doc of passegerdoc.missingCheckinRequiredFields.filter(
          (doc) =>
            !doc.document.includes('PERMANENT_RESIDENT_CARD') &&
            !omitMissingCheckinRequiredFields.includes(doc.document)
        )) {
          const validatePassportInf = currentPassanger.missingCheckinRequiredFields.find(
            (docu) => docu.document === 'PASSPORT/INF'
          );
          if (validatePassportInf === undefined) {
            passengerADT.missingCheckinRequiredFields.push(doc);
          }
        }
      }

      if (
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF
        ) === undefined
      ) {
        const passportInf = passengerADT.missingCheckinRequiredFields.find(
          (doc) => doc.document === 'PASSPORT/INF'
        );
        if (passportInf) {
          passportInf.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
          passportInf.save = !!currentPassanger.passengerInfo.passport?.documentNumber;
        }
      }
      if (
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS_INF
        ) === undefined
      ) {
        const document = passengerADT.missingCheckinRequiredFields.find(
          (doc) => doc.document === 'DESTINATION_ADDRESS/INF'
        );
        if (document) {
          const save = !!currentPassanger.passengerInfo.destinationAddress?.city;
          document.status = save;
          document.save = save;
        }
      }
    }

    let newData: MissingCheckinRequiredFields;
    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT
      ) === undefined
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.passport-text';
      newData.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
      newData.save = !!currentPassanger.passengerInfo.passport?.documentNumber;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);

      if (
        currentPassanger.passengerInfant &&
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF
        ) === undefined
      ) {
        newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF);
        newData.preview = 'Required';
        newData.isRequired = true;
        newData.title = 'documents.passport-text';
        newData.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
        newData.save = !!passengerINF.passengerInfo.passport?.documentNumber;
        currentPassanger.missingCheckinRequiredFields.unshift(newData);
      }
    }
    if (
      currentPassanger.missingCheckinRequiredFields.find((x) => x.document.includes('VISA')) ===
        undefined &&
      currentPassanger.passengerInfo.visa &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      const countryCode = response.passengers.find(
        (p) => p.passengerNameNumber === currentPassanger.nameRefNumber
      );
      const aplicableCode = countryCode?.timaticInfoList
        ? countryCode?.timaticInfoList[0]?.country?.countryCode
        : undefined;
      currentPassanger.applicationVisaContry = aplicableCode;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.VISA);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'paxForms.visa.visa_name';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers
        .find((p) => p.passengerNameNumber === currentPassanger.passengerInfant)
        ?.missingCheckinRequiredFields.find((d) => d.document.includes('VISA')) === undefined &&
      passengerINF?.passengerInfo?.visa
    ) {
      const countryCode = response.passengers.find(
        (p) => p.passengerNameNumber === currentPassanger.nameRefNumber
      );
      const aplicableCode = countryCode?.timaticInfoList
        ? countryCode?.timaticInfoList[0]?.country?.countryCode
        : undefined;
      currentPassanger.applicationVisaContry = aplicableCode;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.VISA_INF);
      newData.title = 'paxForms.visa.visa_name';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS
      ) === undefined &&
      currentPassanger.passengerInfo.destinationAddress &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.desAddrs-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers
        .find((p) => p.passengerNameNumber === currentPassanger.passengerInfant)
        ?.missingCheckinRequiredFields.find((d) => d.document.includes('DESTINATION_ADDRESS')) ===
        undefined &&
      passengerINF?.passengerInfo?.destinationAddress
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS_INF
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.desAddrs-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE
      ) === undefined &&
      currentPassanger.passengerInfo?.timaticRQ?.stayType &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      const totalDoc = currentPassanger?.missingCheckinRequiredFields?.filter(
        (doc) => doc.document === 'RETURN_DATE_CO'
      )?.length;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.tim-text';
      newData.status = true;
      newData.save = true;
      if (totalDoc < 1) {
        currentPassanger.missingCheckinRequiredFields.unshift(newData);
      } else {
        const index = currentPassanger?.missingCheckinRequiredFields?.findIndex(
          (doc) => doc.document === 'RETURN_DATE_CO'
        );
        currentPassanger.missingCheckinRequiredFields[index] = newData;
      }
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers.find(
        (p) =>
          p.passengerNameNumber?.trim() === currentPassanger.passengerInfant?.trim() &&
          !p.missingCheckinRequiredFields?.some((d) => d.document?.startsWith('RETURN_DATE')) &&
          p.timaticRQ?.returnDate
      )
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE_INF);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.tim-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.EMERGENCY_CONTACT
      ) === undefined &&
      currentPassanger.passengerInfo.emergencyContact
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.EMERGENCY_CONTACT);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'contactEmergency.emergency';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find((x) =>
        x.document.includes('PERMANENT_RESIDENT_CARD')
      ) === undefined &&
      currentPassanger.passengerInfo.permanentResidentCard &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.PERMANENT_RESIDENT_CARD
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'recidivism.recidivism';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    const visa = currentPassanger.missingCheckinRequiredFields.find((el) =>
      el.document.includes('VISA')
    );
    if (visa && currentPassanger.applicationVisaContry === null) {
      const [docto, applicationVisaContry] = visa.document.split('_');
      currentPassanger.applicationVisaContry = applicationVisaContry;
      visa.document = docto;
    }

    const permanentResidentCard = currentPassanger.missingCheckinRequiredFields.find((d) =>
      d.document.includes('PERMANENT_RESIDENT_CARD')
    );
    if (permanentResidentCard && currentPassanger.passengerInfo.permanentResidentCard === null) {
      permanentResidentCard.document = 'PERMANENT_RESIDENT_CARD';
    }

    //ContactTracing
    if (
      passegerdoc.missingCheckinRequiredFields.find((doc) =>
        doc.document.includes('PASSENGER_TRACING_CONTACT')
      )
    ) {
      currentPassanger.requiredContactTracing = true;
    }

    currentPassanger.changeStatus();
  }

  showMessageUpdateDocuments() {
    const error = new ErrorModel().withCode('99').withMessage('checkin.update-documents');
    return this._message.error(
      {
        typesError: TypesErrors.UPDATE_DOCUMENTS,
        error,
      },
      null,
      TypesErrors.UPDATE_DOCUMENTS,
      messageParamsUpdateDocuments
    );
  }

  next() {
    const passengersSelection = this.getPassengerSelection();

    for (const pax of passengersSelection) {
      pax.changeStatus();
    }

    const current = this.getCurrentPassenger();
    const passengers = this.getPassengerSelection().filter(
      (pax) => pax.estatusCheckin === ESTATUS_CHECKIN.NOT_DOCUMENTS || !pax.checkinStatusDocuments
    );

    if (passengers.length > 0) {
      if (current) {
        const indxCurrent = passengers.findIndex(
          (pax) => pax.nameRefNumber === current.nameRefNumber
        );

        if (indxCurrent !== -1 && passengers.length > 1 && indxCurrent < passengers.length - 1) {
          const passenger = passengers[indxCurrent + 1];
          this.setPositionCurrentPassenger(indxCurrent + 1);
          this.setSelectCurrentPassenger(passenger);

          return passenger;
        }

        this.setSelectCurrentPassenger(passengers[0]);
        return passengers[0];
      }
      this.setSelectCurrentPassenger(passengers[0]);
      return passengers[0];
    }

    this.setSelectCurrentPassenger(undefined);
    return null;
  }

  /**
   * Arma la cola de verificación de pasaporte con los `nameRefNumber` de los
   * pasajeros que tienen pasaporte pendiente según la regla vigente.
   * Se guarda el `nameRefNumber` y no el objeto porque `setPassenger` reescribe
   * los pasajeros del store (causa raíz 4).
   * @param passengers Lista de pasajeros a evaluar.
   */
  startPassportVerification(passengers: Passenger[]): void {
    this.passportVerificationQueue = pendingPassportPassengers(passengers).map(
      (passenger) => passenger.nameRefNumber
    );
  }

  /**
   * Remueve un pasajero de la cola de verificación de pasaporte por su
   * `nameRefNumber`.
   * @param passenger Pasajero que completó la verificación.
   */
  completePassportVerification(passenger: Passenger): void {
    this.passportVerificationQueue = this.passportVerificationQueue.filter(
      (nameRefNumber) => nameRefNumber !== passenger?.nameRefNumber
    );
  }

  /**
   * Retorna el siguiente pasajero pendiente de verificación de pasaporte
   * resolviendo el `nameRefNumber` de la cola contra el store, o `null` si la
   * cola está vacía o el pasajero ya no existe en la selección.
   */
  getNextPassportPending(): Passenger | null {
    const [nextNameRefNumber] = this.passportVerificationQueue;
    if (!nextNameRefNumber) {
      return null;
    }
    return (
      this.getPassengerSelection().find((pax) => pax.nameRefNumber === nextNameRefNumber) ?? null
    );
  }

  /**
   * Vacía la cola de verificación de pasaporte.
   */
  clearPassportVerification(): void {
    this.passportVerificationQueue = [];
  }

  incrementPaxStepInt() {
    const current = this.paxStepIntSubject.getValue();
    this.paxStepIntSubject.next(current + 1);
  }

  clearPaxStepInt() {
    this.paxStepIntSubject.next(1);
  }

  prev() {
    const current = this.getCurrentPassenger();
    const passengers = this.getPassengerSelection().filter((pax) =>
      [ESTATUS_CHECKIN.NOT_DOCUMENTS].includes(pax.estatusCheckin)
    );
    if (passengers.length > 0) {
      if (current) {
        const indxCurrent = passengers.findIndex(
          (pax) => pax.nameRefNumber === current.nameRefNumber
        );
        if (indxCurrent !== -1) {
          const passenger = passengers[indxCurrent - 1];
          this.setPositionCurrentPassenger(indxCurrent - 1);
          this.setSelectCurrentPassenger(passenger);
          return passenger;
        }
        this.setSelectCurrentPassenger(passengers[0]);
        return passengers[0];
      }
      this.setSelectCurrentPassenger(passengers[0]);
      return passengers[0];
    }
    this.setSelectCurrentPassenger(undefined);
    return null;
  }

  getInfantByAdult(passenger: Passenger): Passenger | undefined {
    const passengers = this.getPassengerSelection(true);
    return passengers.find(
      (pax) => pax.nameRefNumber === passenger.passengerInfant && pax.paxType === PAX_TYPE.INFANT
    );
  }

  //#region Bags
  assignBenefitsToPassenger(reservation: Reservation, response: RespBenefits[]) {
    for (let pLoyalty of response) {
      const passenger = reservation.passengers.find(
        (p) => pLoyalty.accountNumber === `00${p.passengerInfo?.ffNumber}`
      );
      if (passenger) {
        passenger.benefits = pLoyalty;

        // Si ya redimió, preservar los tipos basados en el método de redención
        const redeemedBag = passenger.bagStack?.find(
          (bag) =>
            bag.status === StatusBag.REDEEMED_APPLIED &&
            (bag.isRedemptionTier ||
              bag.isRedemptionCobrand ||
              bag.isRedemptionCorporate ||
              bag.isRedemptionFF)
        );
        const redeemedCarryOn = passenger.bagStackCarryon?.find(
          (bag) =>
            bag.status === StatusBag.REDEEMED_APPLIED &&
            (bag.isRedemptionTier ||
              bag.isRedemptionCobrand ||
              bag.isRedemptionCorporate ||
              bag.isRedemptionFF)
        );
        if (redeemedBag) {
          passenger.typeTier = redeemedBag.isRedemptionTier ? 'tier' : null;
          passenger.typeCobrand = redeemedBag.isRedemptionCobrand ? 'cobrand' : null;
          passenger.typeCobrandCarryOn = null;
          passenger.typeff = redeemedBag.isRedemptionFF ? 'f&f' : null;
          passenger.typeCorporate = redeemedBag.isRedemptionCorporate ? 'corporate' : null;
        } else if (redeemedCarryOn) {
          passenger.typeTier = redeemedCarryOn.isRedemptionTier ? 'tier' : null;
          passenger.typeCobrand = null;
          passenger.typeCobrandCarryOn = redeemedCarryOn.isRedemptionCobrand ? 'cobrand' : null;
          passenger.typeff = null;
          passenger.typeCorporate = null;
        } else if (
          (pLoyalty as any).isNotFirstBagagge &&
          pLoyalty.cobrandCode &&
          !pLoyalty.cobrandBenefits?.carryOn &&
          !pLoyalty.cobrandBenefits?.baggage
        ) {
          const isMainLight = reservation.flightSummary?.fareFamily === FARES_FAMILY.MAIN_LIGHT;
          const paidCarryOn =
            isMainLight &&
            passenger.bagStackCarryon?.find(
              (bag) => bag.rficSubcode === '0CZ' && bag.status === StatusBag.PAID && !bag.used
            );
          if (paidCarryOn) {
            paidCarryOn.status = StatusBag.REDEEMED_APPLIED;
            paidCarryOn.isRedemptionCobrand = true;
            passenger.typeCobrandCarryOn = 'cobrand';
          }
          passenger.typeTier = passenger.benefits?.tierBenefits?.baggage ? 'tier' : null;
          passenger.typeCobrand = passenger.benefits?.cobrandBenefits?.baggage ? 'cobrand' : null;
          passenger.typeff = passenger.benefits?.ffBenefits?.baggage ? 'f&f' : null;
          passenger.typeCorporate = (passenger.corporate as any)?.corporateBenefits?.baggage
            ? 'corporate'
            : null;
          passenger.buildStackBagsBenifit();
          passenger.buildStackCarryOnBenifit();
        } else {
          passenger.typeTier = passenger.benefits?.tierBenefits?.baggage ? 'tier' : null;
          passenger.typeCobrand = passenger.benefits?.cobrandBenefits?.baggage ? 'cobrand' : null;
          const isMainLight = reservation.flightSummary?.fareFamily === FARES_FAMILY.MAIN_LIGHT;
          passenger.typeCobrandCarryOn =
            isMainLight && passenger.benefits?.cobrandBenefits?.carryOn ? 'cobrand' : null;
          passenger.typeff = passenger.benefits?.ffBenefits?.baggage ? 'f&f' : null;
          passenger.typeCorporate = (passenger.corporate as any)?.corporateBenefits?.baggage
            ? 'corporate'
            : null;
          passenger.buildStackBagsBenifit();
          passenger.buildStackCarryOnBenifit();
        }
      }
    }
    this._globalService.setReservation(reservation);
  }

  assignIncludeBagsToPassenger(
    reservation: Reservation,
    res: {
      baggageIncludes: {
        nameRefNumber: string;
        offerCheckBags: unknown[];
        offerCarryOn: unknown[];
      }[];
    }
  ) {
    const { passengers } = reservation ?? ({} as Reservation);
    const { baggageIncludes } = res;
    for (let passenger of passengers) {
      const bags = baggageIncludes.find((s) => s.nameRefNumber === passenger.nameRefNumber);
      if (bags) {
        passenger.buildStackBagsInclude(bags.offerCheckBags || []); // Construir el stack de maletas incluidas
        passenger.buildStackBagsCarryOnInclude(bags.offerCarryOn || []); // Construir el stack de maletas de mano incluidas
        // passenger.buildBagsUsedByRemarks(reservation.remarks); // Marcar las maletas usadas por remark TODO cuando se tenga solucion por parte de api revivir esto
      }
    }
    this._globalService.setReservation(reservation);
  }

  reedeemAncillaries(
    passenger: Passenger,
    redemptionBenefitType: 'checkedBag' | 'carryOn' = 'checkedBag'
  ) {
    const modalRef = this._modalService.open(BagsRedemptionModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.passenger = passenger;
    modalRef.componentInstance.reservation = this._globalService.getReservation();
    modalRef.componentInstance.redemptionBenefitType = redemptionBenefitType;
    return modalRef.result;
  }

  reedeemAncillariesForCobrand(passenger: Passenger) {
    const modalRef = this._modalService.open(BenefitOfferModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.passenger = passenger;
    modalRef.componentInstance.reservation = this._globalService.getReservation();
    return modalRef.result;
  }

  reedeemAncillariesForCobrandCarryOn(passenger: Passenger) {
    const modalRef = this._modalService.open(BenefitOfferModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.passenger = passenger;
    modalRef.componentInstance.reservation = this._globalService.getReservation();
    modalRef.componentInstance.onlyCarryOn = true;
    return modalRef.result;
  }

  openRetryErrorModal() {
    const modalRef = this._modalService.open(RetryErrorModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    return modalRef.result;
  }

  setBaggageEligibility(passengers: any[], reservation: Reservation) {
    for (let pax of passengers) {
      const passenger = this.getSelectPassengersToBags()?.find(
        (p) => p.nameRefNumber === pax.passengerNameNumber
      );
      if (passenger) {
        passenger.shortBaggageDrop = pax.shortBaggageDrop;
        for (let ancillary of pax.ancillaries) {
          const bagExist = passenger.bagStack.find(
            (b) => b?.rficSubcode === ancillary?.code && !b.used
          );
          if (bagExist) {
            const segmentCode = reservation?.flightSummary?.currentSegment?.segmentCode;
            bagExist.eligibility =
              ancillary?.eligibility?.find((bag) => segmentCode === bag.segmentCode)
                ?.baggageDropAllowed || false;
            if (
              bagExist.status === StatusBag.INCLUDE ||
              bagExist.status === StatusBag.REDEEMED ||
              bagExist.status === StatusBag.REDEEMED_APPLIED
            ) {
              bagExist.eligibility = true;
            }
          }
        }
      }
    }
    this._globalService.setReservation(reservation);
  }

  allowBaggageWeighing() {
    const hasBagsEligibility = this.getSelectPassengersToBags()?.some((pax) =>
      pax.bagStack.some(
        (bag) =>
          !bag.used &&
          bag.selected &&
          (bag.eligibility ||
            bag.status === StatusBag.PAID ||
            bag.status === StatusBag.REDEEMED_APPLIED)
      )
    );
    return hasBagsEligibility;
  }
  //#endregion Bags
}
